'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useMemo } from 'react';
import Link from 'next/link';
import clsx from 'clsx';
import { ApiInitiative } from '@/lib/api';
import { LazyImage } from '../LazyImage';
import { Calendar, Play, Expand, Clock, Users, Hand, UserCheck } from 'lucide-react';
import { getInitiativeTypeConfig, getInitiativeUrl, formatEventDateRange, INITIATIVE_STAGE_OPTIONS } from '@sizzlenz/shared';
import { useInitiativeDetailSheet } from '@/lib/hooks/useInitiativeDetailSheet';
import { CompactReactionButton } from '../activities/CompactReactionButton';
import type { ApiConnection } from '@/lib/hooks/useConnections';
interface InitiativeCardProps {
  initiative: ApiInitiative;
  variant?: 'default' | 'compact' | 'template';
  /** Show the initiative status badge (e.g. "Gauging Interest") */
  showStatus?: boolean;
  /** Show the owner/entity name on the card */
  showOwner?: boolean;
  /** Control when to display the interest count badge */
  showInterestCount?: 'never' | 'always' | 'non-zero';
  /** When true, shows a badge if the current user has registered interest */
  showUserInterested?: boolean;
  /** Control whether tags are displayed on the card. Defaults to true. */
  showTags?: boolean;
  /** When true, displays assignment badges (name + role) for assigned users/entities */
  showUserAssignments?: boolean;
  /** Assignments (connections) for this initiative. Pass from parent batch-fetch. */
  assignments?: ApiConnection[];
}
export function InitiativeCard(t0) {
  const $ = _c(197);
  const {
    initiative,
    variant: t1,
    showStatus: t2,
    showOwner: t3,
    showInterestCount: t4,
    showUserInterested: t5,
    showTags: t6,
    showUserAssignments: t7,
    assignments: t8
  } = t0;
  const variant = t1 === undefined ? "default" : t1;
  const showStatus = t2 === undefined ? false : t2;
  const showOwner = t3 === undefined ? false : t3;
  const showInterestCount = t4 === undefined ? "never" : t4;
  const showUserInterested = t5 === undefined ? false : t5;
  const showTags = t6 === undefined ? true : t6;
  const showUserAssignments = t7 === undefined ? false : t7;
  let t9;
  if ($[0] !== t8) {
    t9 = t8 === undefined ? [] : t8;
    $[0] = t8;
    $[1] = t9;
  } else {
    t9 = $[1];
  }
  const assignments = t9;
  const [isHovered, setIsHovered] = useState(false);
  const isCompact = variant === "compact";
  const isTemplate = variant === "template";
  const {
    showInitiativeDetail
  } = useInitiativeDetailSheet();
  let T0;
  let assignmentLabels;
  let config;
  let endDate;
  let handleShowDetails;
  let interestCount;
  let isUserInterested;
  let shouldShowInterestCount;
  let startDate;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  if ($[2] !== assignments || $[3] !== initiative || $[4] !== isCompact || $[5] !== isHovered || $[6] !== isTemplate || $[7] !== showInitiativeDetail || $[8] !== showInterestCount || $[9] !== showStatus || $[10] !== showUserAssignments || $[11] !== showUserInterested) {
    t21 = Symbol.for("react.early_return_sentinel");
    bb0: {
      config = getInitiativeTypeConfig(initiative.type);
      let t22;
      if ($[33] !== initiative.id || $[34] !== initiative.type) {
        t22 = getInitiativeUrl(initiative.type, initiative.id);
        $[33] = initiative.id;
        $[34] = initiative.type;
        $[35] = t22;
      } else {
        t22 = $[35];
      }
      const initiativeUrl = t22;
      const imageUrl = (initiative as any).image;
      startDate = initiative.startDate;
      endDate = initiative.endDate;
      interestCount = initiative.engagement?.votes?.interested?.count ?? 0;
      shouldShowInterestCount = showInterestCount === "always" || showInterestCount === "non-zero" && interestCount > 0;
      isUserInterested = showUserInterested && !!initiative.userInterested;
      let t23;
      bb1: {
        if (!showUserAssignments || assignments.length === 0) {
          let t24;
          if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
            t24 = [];
            $[36] = t24;
          } else {
            t24 = $[36];
          }
          t23 = t24;
          break bb1;
        }
        let t24;
        if ($[37] !== assignments) {
          t24 = assignments.map(_temp);
          $[37] = assignments;
          $[38] = t24;
        } else {
          t24 = $[38];
        }
        t23 = t24;
      }
      assignmentLabels = t23;
      let t24;
      if ($[39] !== initiative || $[40] !== showInitiativeDetail) {
        t24 = e => {
          e.preventDefault();
          e.stopPropagation();
          showInitiativeDetail(initiative);
        };
        $[39] = initiative;
        $[40] = showInitiativeDetail;
        $[41] = t24;
      } else {
        t24 = $[41];
      }
      handleShowDetails = t24;
      if (isTemplate) {
        const details = (initiative.data as any)?.details as string | undefined;
        const setupTime = (initiative.data as any)?.setupTime as string | undefined;
        let t25;
        if ($[42] !== initiative.name) {
          t25 = <h3 className="text-lg font-bold text-gray-900 mb-2">{initiative.name}</h3>;
          $[42] = initiative.name;
          $[43] = t25;
        } else {
          t25 = $[43];
        }
        let t26;
        if ($[44] !== initiative.description) {
          t26 = initiative.description && <p className="text-gray-500 text-sm mb-4 leading-relaxed whitespace-pre-line">{initiative.description}</p>;
          $[44] = initiative.description;
          $[45] = t26;
        } else {
          t26 = $[45];
        }
        let t27;
        if ($[46] !== details) {
          t27 = details && <p className="text-sm text-gray-600 mb-5 flex-1 whitespace-pre-line leading-relaxed">{details}</p>;
          $[46] = details;
          $[47] = t27;
        } else {
          t27 = $[47];
        }
        let t28;
        if ($[48] !== setupTime) {
          t28 = setupTime && <div className="flex items-center gap-1.5 text-gray-400 text-xs"><Clock className="h-3.5 w-3.5" /><span>{setupTime}</span></div>;
          $[48] = setupTime;
          $[49] = t28;
        } else {
          t28 = $[49];
        }
        let t29;
        if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
          t29 = <span className="text-xs font-semibold text-primary-600 group-hover:text-primary-700 ml-auto">View this starter →</span>;
          $[50] = t29;
        } else {
          t29 = $[50];
        }
        let t30;
        if ($[51] !== t28) {
          t30 = <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-auto">{t28}{t29}</div>;
          $[51] = t28;
          $[52] = t30;
        } else {
          t30 = $[52];
        }
        let t31;
        if ($[53] !== t25 || $[54] !== t26 || $[55] !== t27 || $[56] !== t30) {
          t31 = <div className="group rounded-2xl border border-green-100 bg-white p-7 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 h-full flex flex-col">{t25}{t26}{t27}{t30}</div>;
          $[53] = t25;
          $[54] = t26;
          $[55] = t27;
          $[56] = t30;
          $[57] = t31;
        } else {
          t31 = $[57];
        }
        let t32;
        if ($[58] !== initiativeUrl || $[59] !== t31) {
          t32 = <Link href={initiativeUrl} className="h-full cursor-pointer">{t31}</Link>;
          $[58] = initiativeUrl;
          $[59] = t31;
          $[60] = t32;
        } else {
          t32 = $[60];
        }
        t21 = t32;
        break bb0;
      }
      T0 = Link;
      t19 = initiativeUrl;
      t20 = "h-full";
      const t25 = isCompact ? "rounded-xl" : "rounded-2xl";
      const t26 = !isCompact && "border border-gray-100";
      if ($[61] !== t25 || $[62] !== t26) {
        t16 = clsx("group relative bg-white overflow-hidden hover:shadow-xl cursor-pointer h-full flex flex-col", t25, t26);
        $[61] = t25;
        $[62] = t26;
        $[63] = t16;
      } else {
        t16 = $[63];
      }
      if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
        t17 = () => setIsHovered(true);
        t18 = () => setIsHovered(false);
        $[64] = t17;
        $[65] = t18;
      } else {
        t17 = $[64];
        t18 = $[65];
      }
      t13 = "relative overflow-hidden bg-gray-50";
      const t27 = imageUrl || "";
      const t28 = initiative.name;
      const t29 = isCompact ? "16/9" : "3/2";
      const t30 = isCompact ? "h-[140px]" : "h-[240px]";
      const t31 = isHovered && "scale-105 transition-transform duration-500";
      let t32;
      if ($[66] !== t30 || $[67] !== t31) {
        t32 = clsx("w-full", t30, t31);
        $[66] = t30;
        $[67] = t31;
        $[68] = t32;
      } else {
        t32 = $[68];
      }
      const t33 = isCompact ? "h-[140px]" : "h-[240px]";
      let t34;
      if ($[69] !== t33) {
        t34 = clsx("flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50", t33);
        $[69] = t33;
        $[70] = t34;
      } else {
        t34 = $[70];
      }
      const t35 = isCompact ? "w-8 h-8" : "w-16 h-16";
      let t36;
      if ($[71] !== t35) {
        t36 = clsx("mx-auto text-indigo-300 mb-2", t35);
        $[71] = t35;
        $[72] = t36;
      } else {
        t36 = $[72];
      }
      let t37;
      if ($[73] !== t36) {
        t37 = <Play className={t36} />;
        $[73] = t36;
        $[74] = t37;
      } else {
        t37 = $[74];
      }
      let t38;
      if ($[75] !== isCompact) {
        t38 = !isCompact && <p className="text-indigo-400 text-sm font-medium">No cover image</p>;
        $[75] = isCompact;
        $[76] = t38;
      } else {
        t38 = $[76];
      }
      let t39;
      if ($[77] !== t37 || $[78] !== t38) {
        t39 = <div className="text-center p-2">{t37}{t38}</div>;
        $[77] = t37;
        $[78] = t38;
        $[79] = t39;
      } else {
        t39 = $[79];
      }
      let t40;
      if ($[80] !== t34 || $[81] !== t39) {
        t40 = <div className={t34}>{t39}</div>;
        $[80] = t34;
        $[81] = t39;
        $[82] = t40;
      } else {
        t40 = $[82];
      }
      if ($[83] !== initiative.name || $[84] !== t27 || $[85] !== t29 || $[86] !== t32 || $[87] !== t40) {
        t14 = <LazyImage src={t27} alt={t28} aspectRatio={t29} className={t32} objectFit="cover" fallback={t40} />;
        $[83] = initiative.name;
        $[84] = t27;
        $[85] = t29;
        $[86] = t32;
        $[87] = t40;
        $[88] = t14;
      } else {
        t14 = $[88];
      }
      if ($[89] === Symbol.for("react.memo_cache_sentinel")) {
        t15 = <div className={clsx("absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 transition-opacity duration-300")} />;
        $[89] = t15;
      } else {
        t15 = $[89];
      }
      const t41 = isCompact ? "top-2 right-2" : "top-3 right-3";
      if ($[90] !== t41) {
        t11 = clsx("absolute z-10 flex items-center gap-1.5", t41);
        $[90] = t41;
        $[91] = t11;
      } else {
        t11 = $[91];
      }
      if ($[92] !== initiative.status || $[93] !== isCompact || $[94] !== showStatus) {
        t12 = showStatus && initiative.status !== "active" && <span className={clsx("rounded-full font-semibold shadow-sm bg-white/90 text-gray-700 backdrop-blur-sm", isCompact ? "text-[10px] px-2 py-0.5" : "text-xs px-3 py-1")}>{INITIATIVE_STAGE_OPTIONS.find(opt => opt.value === initiative.status)?.label || initiative.status}</span>;
        $[92] = initiative.status;
        $[93] = isCompact;
        $[94] = showStatus;
        $[95] = t12;
      } else {
        t12 = $[95];
      }
      t10 = clsx("rounded-full font-semibold shadow-sm", isCompact ? "text-[10px] px-2 py-0.5" : "text-xs px-3 py-1", config.color);
    }
    $[2] = assignments;
    $[3] = initiative;
    $[4] = isCompact;
    $[5] = isHovered;
    $[6] = isTemplate;
    $[7] = showInitiativeDetail;
    $[8] = showInterestCount;
    $[9] = showStatus;
    $[10] = showUserAssignments;
    $[11] = showUserInterested;
    $[12] = T0;
    $[13] = assignmentLabels;
    $[14] = config;
    $[15] = endDate;
    $[16] = handleShowDetails;
    $[17] = interestCount;
    $[18] = isUserInterested;
    $[19] = shouldShowInterestCount;
    $[20] = startDate;
    $[21] = t10;
    $[22] = t11;
    $[23] = t12;
    $[24] = t13;
    $[25] = t14;
    $[26] = t15;
    $[27] = t16;
    $[28] = t17;
    $[29] = t18;
    $[30] = t19;
    $[31] = t20;
    $[32] = t21;
  } else {
    T0 = $[12];
    assignmentLabels = $[13];
    config = $[14];
    endDate = $[15];
    handleShowDetails = $[16];
    interestCount = $[17];
    isUserInterested = $[18];
    shouldShowInterestCount = $[19];
    startDate = $[20];
    t10 = $[21];
    t11 = $[22];
    t12 = $[23];
    t13 = $[24];
    t14 = $[25];
    t15 = $[26];
    t16 = $[27];
    t17 = $[28];
    t18 = $[29];
    t19 = $[30];
    t20 = $[31];
    t21 = $[32];
  }
  if (t21 !== Symbol.for("react.early_return_sentinel")) {
    return t21;
  }
  let t22;
  if ($[96] !== config.label || $[97] !== t10) {
    t22 = <span className={t10}>{config.label}</span>;
    $[96] = config.label;
    $[97] = t10;
    $[98] = t22;
  } else {
    t22 = $[98];
  }
  let t23;
  if ($[99] !== t11 || $[100] !== t12 || $[101] !== t22) {
    t23 = <div className={t11}>{t12}{t22}</div>;
    $[99] = t11;
    $[100] = t12;
    $[101] = t22;
    $[102] = t23;
  } else {
    t23 = $[102];
  }
  let t24;
  if ($[103] !== isCompact) {
    t24 = false && <span className={clsx("absolute bg-emerald-500 text-white font-semibold rounded-full shadow-lg z-10 flex items-center gap-1", isCompact ? "top-2 right-2 text-[10px] px-2 py-0.5" : "top-3 right-3 text-xs px-3 py-1")}><span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />LIVE</span>;
    $[103] = isCompact;
    $[104] = t24;
  } else {
    t24 = $[104];
  }
  const t25 = isCompact ? "top-2 left-2 w-7 h-7" : "top-3 left-3 w-10 h-10";
  let t26;
  if ($[105] !== t25) {
    t26 = clsx("absolute rounded-full shadow-lg flex items-center justify-center cursor-pointer transition-all duration-200", t25, "opacity-100 translate-y-0", "bg-white/95 backdrop-blur-sm hover:bg-primary-500 hover:text-white");
    $[105] = t25;
    $[106] = t26;
  } else {
    t26 = $[106];
  }
  const t27 = isCompact ? "w-3 h-3" : "w-5 h-5";
  let t28;
  if ($[107] !== t27) {
    t28 = clsx(t27);
    $[107] = t27;
    $[108] = t28;
  } else {
    t28 = $[108];
  }
  let t29;
  if ($[109] !== t28) {
    t29 = <Expand className={t28} />;
    $[109] = t28;
    $[110] = t29;
  } else {
    t29 = $[110];
  }
  let t30;
  if ($[111] !== handleShowDetails || $[112] !== t26 || $[113] !== t29) {
    t30 = <button onClick={handleShowDetails} className={t26} aria-label="Show details" title="Show details">{t29}</button>;
    $[111] = handleShowDetails;
    $[112] = t26;
    $[113] = t29;
    $[114] = t30;
  } else {
    t30 = $[114];
  }
  let t31;
  if ($[115] !== initiative.id) {
    t31 = initiative.id.toString();
    $[115] = initiative.id;
    $[116] = t31;
  } else {
    t31 = $[116];
  }
  const t32 = initiative.name;
  let t33;
  if ($[117] !== initiative.userReactions) {
    t33 = initiative.userReactions || [];
    $[117] = initiative.userReactions;
    $[118] = t33;
  } else {
    t33 = $[118];
  }
  let t34;
  if ($[119] !== initiative.engagement) {
    t34 = initiative.engagement || {
      reactions: {
        total: 0,
        by_type: {}
      },
      votes: {
        up: {
          count: 0,
          last_at: ""
        },
        down: {
          count: 0,
          last_at: ""
        }
      }
    };
    $[119] = initiative.engagement;
    $[120] = t34;
  } else {
    t34 = $[120];
  }
  const t35 = isCompact ? "bottom-2 left-2" : "bottom-3 left-3";
  let t36;
  if ($[121] !== t35) {
    t36 = clsx("absolute", t35, "opacity-100 translate-y-0");
    $[121] = t35;
    $[122] = t36;
  } else {
    t36 = $[122];
  }
  let t37;
  if ($[123] !== initiative.name || $[124] !== t31 || $[125] !== t33 || $[126] !== t34 || $[127] !== t36 || $[128] !== variant) {
    t37 = <CompactReactionButton objectId={t31} objectType="initiative" variant={variant} title={t32} userReactions={t33} engagementSummary={t34} className={t36} />;
    $[123] = initiative.name;
    $[124] = t31;
    $[125] = t33;
    $[126] = t34;
    $[127] = t36;
    $[128] = variant;
    $[129] = t37;
  } else {
    t37 = $[129];
  }
  let t38;
  if ($[130] !== t13 || $[131] !== t14 || $[132] !== t15 || $[133] !== t23 || $[134] !== t24 || $[135] !== t30 || $[136] !== t37) {
    t38 = <div className={t13}>{t14}{t15}{t23}{t24}{t30}{t37}</div>;
    $[130] = t13;
    $[131] = t14;
    $[132] = t15;
    $[133] = t23;
    $[134] = t24;
    $[135] = t30;
    $[136] = t37;
    $[137] = t38;
  } else {
    t38 = $[137];
  }
  const t39 = isCompact ? "p-3" : "p-5";
  let t40;
  if ($[138] !== t39) {
    t40 = clsx("flex-1", t39);
    $[138] = t39;
    $[139] = t40;
  } else {
    t40 = $[139];
  }
  let t41;
  if ($[140] !== endDate || $[141] !== startDate) {
    t41 = (startDate || endDate) && <div className="flex items-center gap-1.5 text-gray-500 text-xs mb-2"><Calendar className="w-3.5 h-3.5" /><span>{formatEventDateRange(startDate, endDate)}</span></div>;
    $[140] = endDate;
    $[141] = startDate;
    $[142] = t41;
  } else {
    t41 = $[142];
  }
  const t42 = isCompact ? "text-sm mb-1" : "text-[17px] leading-tight mb-2";
  let t43;
  if ($[143] !== t42) {
    t43 = clsx("font-bold text-gray-900 line-clamp-2 group-hover:text-indigo-600 transition-colors", t42);
    $[143] = t42;
    $[144] = t43;
  } else {
    t43 = $[144];
  }
  let t44;
  if ($[145] !== initiative.name || $[146] !== t43) {
    t44 = <h3 className={t43}>{initiative.name}</h3>;
    $[145] = initiative.name;
    $[146] = t43;
    $[147] = t44;
  } else {
    t44 = $[147];
  }
  let t45;
  if ($[148] !== initiative.owner || $[149] !== isCompact || $[150] !== showOwner) {
    t45 = showOwner && initiative.owner?.name && <p className={clsx("text-gray-500 truncate", isCompact ? "text-xs mb-1" : "text-sm mb-2")}>{initiative.owner.name}</p>;
    $[148] = initiative.owner;
    $[149] = isCompact;
    $[150] = showOwner;
    $[151] = t45;
  } else {
    t45 = $[151];
  }
  let t46;
  if ($[152] !== initiative.description || $[153] !== isCompact) {
    t46 = initiative.description && !isCompact && <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed">{initiative.description}</p>;
    $[152] = initiative.description;
    $[153] = isCompact;
    $[154] = t46;
  } else {
    t46 = $[154];
  }
  let t47;
  if ($[155] !== interestCount || $[156] !== isCompact || $[157] !== shouldShowInterestCount) {
    t47 = shouldShowInterestCount && <div className={clsx("flex items-center gap-1.5 text-emerald-700", isCompact ? "text-xs mt-1" : "text-sm mt-2")}><Users className={clsx(isCompact ? "w-3 h-3" : "w-4 h-4")} /><span className="font-medium">{interestCount} interested</span></div>;
    $[155] = interestCount;
    $[156] = isCompact;
    $[157] = shouldShowInterestCount;
    $[158] = t47;
  } else {
    t47 = $[158];
  }
  let t48;
  if ($[159] !== isCompact || $[160] !== isUserInterested) {
    t48 = isUserInterested && <div className={clsx("flex items-center gap-1.5 text-yellow-700", isCompact ? "text-xs mt-1" : "text-sm mt-2")}><Hand className={clsx(isCompact ? "w-3 h-3" : "w-4 h-4", "fill-yellow-200")} /><span className="font-medium">You're interested</span></div>;
    $[159] = isCompact;
    $[160] = isUserInterested;
    $[161] = t48;
  } else {
    t48 = $[161];
  }
  let t49;
  if ($[162] !== assignmentLabels || $[163] !== isCompact || $[164] !== showUserAssignments) {
    t49 = showUserAssignments && assignmentLabels.length > 0 && <div className={clsx("flex items-center gap-1.5 text-indigo-700", isCompact ? "text-xs mt-1" : "text-sm mt-2")}><UserCheck className={clsx(isCompact ? "w-3 h-3" : "w-4 h-4")} /><div className="flex flex-wrap gap-1">{assignmentLabels.map(_temp2)}</div></div>;
    $[162] = assignmentLabels;
    $[163] = isCompact;
    $[164] = showUserAssignments;
    $[165] = t49;
  } else {
    t49 = $[165];
  }
  let t50;
  if ($[166] !== assignmentLabels || $[167] !== initiative.userAssignments || $[168] !== isCompact || $[169] !== showUserAssignments) {
    t50 = showUserAssignments && assignmentLabels.length === 0 && initiative.userAssignments && initiative.userAssignments.length >= 0 && <div className={clsx("flex items-center gap-1.5 text-indigo-700", isCompact ? "text-xs mt-1" : "text-sm mt-2")}><UserCheck className={clsx(isCompact ? "w-3 h-3" : "w-4 h-4")} /><span className="font-medium">{initiative.userAssignments.length > 0 ? initiative.userAssignments.join(", ") : "You're assigned"}</span></div>;
    $[166] = assignmentLabels;
    $[167] = initiative.userAssignments;
    $[168] = isCompact;
    $[169] = showUserAssignments;
    $[170] = t50;
  } else {
    t50 = $[170];
  }
  let t51;
  if ($[171] !== initiative.tags || $[172] !== isCompact || $[173] !== showTags) {
    t51 = showTags && initiative.tags && initiative.tags.length > 0 && !isCompact && <div className="mt-4 flex flex-wrap gap-1.5">{initiative.tags.slice(0, 3).map(_temp3)}{initiative.tags.length > 3 && <span className="bg-gray-50 text-gray-500 text-[11px] px-2.5 py-1 rounded-md font-medium">+{initiative.tags.length - 3}</span>}</div>;
    $[171] = initiative.tags;
    $[172] = isCompact;
    $[173] = showTags;
    $[174] = t51;
  } else {
    t51 = $[174];
  }
  let t52;
  if ($[175] !== t40 || $[176] !== t41 || $[177] !== t44 || $[178] !== t45 || $[179] !== t46 || $[180] !== t47 || $[181] !== t48 || $[182] !== t49 || $[183] !== t50 || $[184] !== t51) {
    t52 = <div className={t40}>{t41}{t44}{t45}{t46}{t47}{t48}{t49}{t50}{t51}</div>;
    $[175] = t40;
    $[176] = t41;
    $[177] = t44;
    $[178] = t45;
    $[179] = t46;
    $[180] = t47;
    $[181] = t48;
    $[182] = t49;
    $[183] = t50;
    $[184] = t51;
    $[185] = t52;
  } else {
    t52 = $[185];
  }
  let t53;
  if ($[186] !== t16 || $[187] !== t17 || $[188] !== t18 || $[189] !== t38 || $[190] !== t52) {
    t53 = <div className={t16} onMouseEnter={t17} onMouseLeave={t18}>{t38}{t52}</div>;
    $[186] = t16;
    $[187] = t17;
    $[188] = t18;
    $[189] = t38;
    $[190] = t52;
    $[191] = t53;
  } else {
    t53 = $[191];
  }
  let t54;
  if ($[192] !== T0 || $[193] !== t19 || $[194] !== t20 || $[195] !== t53) {
    t54 = <T0 href={t19} className={t20}>{t53}</T0>;
    $[192] = T0;
    $[193] = t19;
    $[194] = t20;
    $[195] = t53;
    $[196] = t54;
  } else {
    t54 = $[196];
  }
  return t54;
}
function _temp3(tag) {
  return <span key={tag} className="bg-gray-100 text-gray-600 text-[11px] px-2.5 py-1 rounded-md font-medium">{tag}</span>;
}
function _temp2(a) {
  return <span key={a.id} className="inline-flex items-center bg-indigo-50 text-indigo-700 text-[11px] px-2 py-0.5 rounded-md font-medium">{a.name}{a.role ? ` · ${a.role}` : ""}</span>;
}
function _temp(conn) {
  const isEntity = conn.fromType === "entity";
  const fromUser = (conn as any).fromUser as {
    name?: string | null;
    email?: string;
  } | undefined;
  const fromEntity = (conn as any).fromEntity as {
    name_1?: string | null;
  } | undefined ?? conn.entity;
  const name = isEntity ? fromEntity?.name_1 || "Entity" : fromUser?.name || fromUser?.email || "User";
  const roles = conn.data?.relationships as string[] ?? (conn.data?.role ? [conn.data.role as string] : []);
  return {
    id: conn.id,
    name,
    role: roles.join(", ")
  };
}